import type { CityConfig } from "@/pages/city-template/types";

export const sunValleyConfig: CityConfig = {
  city: "Sun Valley",
  state: "Nevada",
  stateAbbr: "NV",
  slug: "sun-valley",
  route: "/cities/sun-valley",
  title: "Emergency & Expedited Passport Services Sun Valley NV | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Sun Valley, NV — as fast as 24 hours. No Sun Valley passport agency. Fast Passport Center couriers to the San Francisco Passport Agency at the Phillip Burton Federal Building, about 230 miles away. A+ BBB rated. Serving Cold Springs, North Valleys, Lemmon Valley, and all northern Washoe County travelers heading abroad for work, recreation, or family.",
  h1: "Emergency & Expedited Passport Services in Sun Valley, NV",
  heroSubtitle: "Sun Valley is a census-designated community in northern Washoe County, sitting just north of Reno along the US-395 corridor at the base of the Virginia Range. With a population of around 20,000, Sun Valley serves as a residential anchor for families, tradespeople, and professionals who work throughout the Reno-Sparks metro area. Its position between the North Valleys and the Truckee Meadows makes it a gateway community for those heading into Reno for work and services while enjoying the more rural, high-desert lifestyle of northern Washoe County. Fast Passport Center's registered couriers deliver your documents to the San Francisco Passport Agency at the Phillip Burton Federal Building so you get a new or renewed passport as fast as 24 hours — whether you're a Sun Valley tradesperson heading to an international job site, a North Valleys family planning a trip abroad, or a Reno-area professional whose passport expired between business trips.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Sun%20Valley%20Nevada%20high%20desert%20landscape%20with%20Virginia%20Range%20mountains%20in%20background%20during%20golden%20hour%2C%20northern%20Nevada%20residential%20community%20with%20sagebrush%20and%20dramatic%20desert%20sky%2C%20wide%20angle%20photography%20with%20warm%20desert%20tones%20and%20expansive%20mountain%20horizon%20at%20sunset&width=1800&height=700&seq=sun-valley-nv-hero-001&orientation=landscape",
  heroImageAlt: "Sun Valley Nevada high desert with Virginia Range mountains at golden hour",
  geoPosition: "39.5963;-119.7760",
  geoPlacename: "Sun Valley, Nevada",
  lastModified: "2026-06-06",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "Phillip Burton Federal Building, 450 Golden Gate Avenue, San Francisco, CA 94102",
    distance: "Approximately 230 miles from Sun Valley",
  },
  nearbyCitiesZips: [
    { name: "Sun Valley", zips: "89433" },
    { name: "Cold Springs", zips: "89508" },
    { name: "Lemmon Valley", zips: "89506" },
    { name: "Stead", zips: "89506" },
    { name: "Reno", zips: "89501, 89502, 89503, 89506, 89509, 89511, 89512, 89519, 89521" },
    { name: "Sparks", zips: "89431, 89434, 89436, 89441" },
    { name: "Spanish Springs", zips: "89436, 89441" },
    { name: "Panther Valley", zips: "89441" },
    { name: "Verdi", zips: "89439" },
    { name: "Mogul", zips: "89523" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Courier delivery to San Francisco Passport Agency — about 230 miles from Sun Valley via I-80" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "North Valleys Service", desc: "Serving Sun Valley, Cold Springs, Lemmon Valley, and all northern Washoe County" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your Sun Valley case from start to finish" },
  ],
  internalLinks: [
    { name: "Emergency passport in Nevada", route: "/states/nevada" },
    { name: "Expedited passport in Reno", route: "/cities/reno" },
    { name: "Urgent passport in Sun Valley NV", route: "/cities/sun-valley" },
    { name: "Nevada passport hub", route: "/states/nevada" },
    { name: "Fast passport in Sparks NV", route: "/cities/sparks" },
    { name: "Expedited passport in Carson City", route: "/cities/carson-city" },
    { name: "Emergency passport in Fernley NV", route: "/cities/fernley" },
  ],
  fpcOfficeAddress: "355 S Grand Ave, Ste 2450, Los Angeles, California 90071",
  fpcOfficeNote: "Fast Passport Center serves Sun Valley remotely via secure courier. Our registered couriers hand-deliver your documents to the San Francisco Passport Agency at the Phillip Burton Federal Building in downtown San Francisco. Sun Valley's location along the US-395 corridor — just 10 minutes north of Reno and Reno-Tahoe International Airport (RNO) — makes overnight courier routing to the Bay Area consistently reliable via I-80 west. Our dedicated agents ensure Sun Valley and North Valleys travelers never need to make the 4-hour drive to San Francisco for passport processing.",
  fpcOfficeDistance: "Serving all of Sun Valley, North Valleys, Cold Springs, and northern Washoe County",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Sun Valley, Cold Springs, Lemmon Valley, Stead, and all northern Washoe County communities. From tradespeople working on international projects to North Valleys families planning overseas trips, we help Sun Valley travelers get their passports fast.",
  nearbyCitiesIntro: "We serve Sun Valley and nearby communities including Cold Springs, Lemmon Valley, Stead, Spanish Springs, the North Valleys, and the greater Reno-Sparks metro area — all connected by the US-395 corridor.",
  howItWorksIntro: "Most Sun Valley travelers complete everything online and ship documents overnight. At approximately 230 miles from San Francisco, Fast Passport Center provides fast courier turnaround for North Valleys residents — eliminating the 4-hour drive over Donner Pass on I-80 to San Francisco.",
  mistakesIntro: "Sun Valley travelers, including North Valleys tradespeople and Reno-area commuters, face unique passport challenges. Here are the most common mistakes we see.",
  testimonialsIntro: "Sun Valley and North Valleys travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Sun Valley? Here are the most common ones we hear from North Valleys and Washoe County travelers.",
  statePageRoute: "/states/nevada",
  statePageName: "Nevada",
  dmvWebsite: "https://dmv.nv.gov/",
  dmvLabel: "Nevada DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins at Golden Gate Avenue",
        "Life-or-death emergencies only",
        "230 miles to San Francisco (4 hours via I-80)",
        "Still requires pre-scheduled NPIC appointment",
        "Donner Pass winter driving can be dangerous",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "No drive or appointment needed",
        "Ideal for North Valleys families",
        "Perfect for Reno-area tradespeople",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from RNO",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Cannot upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "Sun Valley is 230 miles from the San Francisco Passport Agency — our courier service saves North Valleys residents the I-80 drive over Donner Pass.",
      bullets: ["Washoe County coverage", "24–48 hour turnaround available", "No appointment or drive needed"],
      badge: "Fastest in the North Valleys",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Sun Valley adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Mail-in or courier options", "Serving Sun Valley and North Valleys"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support in Sun Valley and Washoe County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for Sun Valley and North Valleys residents with DS-64 form guidance and courier routing to San Francisco.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement via San Francisco agency"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in Washoe County.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change service after marriage, divorce, or legal changes for Sun Valley and Washoe County residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Sun Valley NV", description: "Urgent passport processing for Sun Valley, NV travelers via San Francisco Passport Agency courier — 230 miles from Sun Valley." },
    { name: "Passport Renewal Sun Valley NV", description: "Fast passport renewal for Sun Valley and Washoe County, Nevada residents." },
    { name: "New Passport Sun Valley NV", description: "First-time passport application assistance for Sun Valley and North Valleys area residents." },
    { name: "Lost Passport Sun Valley NV", description: "Passport replacement for lost or stolen passports for Sun Valley and Washoe County residents." },
    { name: "Child Passport Sun Valley NV", description: "Minor passport services for Sun Valley and North Valleys families." },
    { name: "Name Change Passport Sun Valley NV", description: "Passport name change service for Sun Valley, NV residents." },
  ],
  testimonials: [
    {
      name: "Tony M.",
      location: "Sun Valley, NV — Union Electrician",
      rating: 5,
      text: "I'm a journeyman electrician and got a short-notice job on a large commercial project in Vancouver, Canada. Needed a passport renewal fast — mine had expired while I was working a long local job. Fast Passport Center processed it in 6 business days. The courier service meant I didn't have to take time off work to drive to San Francisco. Made it to Vancouver for the project start date.",
      service: "Passport Renewal",
    },
    {
      name: "The R Family",
      location: "Cold Springs, NV",
      rating: 5,
      text: "Family of five in Cold Springs planning a trip to Mexico — first international trip for our three kids. Needed two adult renewals and three first-time child passports. Fast Passport Center walked us through the whole process, including DS-11 requirements at the Lemmon Valley Post Office. All five passports arrived in 9 business days. The kids are still talking about the beaches in Cancún.",
      service: "Family Passport Package",
    },
    {
      name: "Diana L.",
      location: "Lemmon Valley, NV",
      rating: 5,
      text: "I work for a logistics company in Sparks that handles international freight. Got promoted to a role that requires occasional travel to our Asian offices. My passport had been expired for three years — never needed one before. Fast Passport Center got me a new first-time passport fast. The agent explained every step since I'd never done this before. First trip to Singapore was a success.",
      service: "New Passport",
    },
    {
      name: "Mike and Jenny S.",
      location: "Stead, NV",
      rating: 5,
      text: "We live near Stead Airport and had a dream anniversary trip planned to Italy. Three weeks before departure, we realized Jenny's passport was still in her maiden name after our wedding three years ago. Fast Passport Center handled the name change plus expedited processing — new passport arrived in 7 business days. We celebrated our anniversary with pasta and wine in Tuscany on schedule.",
      service: "Name Change + Renewal",
    },
  ],
  facilities: [
    {
      id: "svl-1",
      name: "Lemmon Valley Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "175 Lemmon Dr",
      city: "Reno",
      state: "NV",
      zip: "89506",
      phone: "(775) 677-1212",
      hours: "Mon–Fri: 9:00 AM – 3:30 PM",
      distance: "Lemmon Valley — 5 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Closest acceptance facility to Sun Valley and the North Valleys. Located on Lemmon Drive. Appointment required. Serves Sun Valley, Lemmon Valley, and Stead.",
      icon: "ri-mail-line",
    },
    {
      id: "svl-2",
      name: "Reno Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2000 Vassar St",
      city: "Reno",
      state: "NV",
      zip: "89510",
      phone: "(775) 322-3223",
      hours: "Mon–Fri: 9:00 AM – 5:00 PM",
      distance: "Reno — 10 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Largest acceptance facility in northern Nevada. Extended hours and most appointment availability. About 15 minutes from Sun Valley via US-395.",
      icon: "ri-mail-line",
    },
    {
      id: "svl-3",
      name: "Washoe County Clerk — Reno",
      type: "County Clerk Passport Acceptance",
      address: "1001 E 9th St, Building A",
      city: "Reno",
      state: "NV",
      zip: "89512",
      phone: "(775) 784-7287",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "Reno — 12 miles south",
      website: "https://www.washoecounty.gov",
      websiteLabel: "Washoe County Government",
      notes: "County-level passport acceptance for all Washoe County residents. More appointment availability than post offices. Located in downtown Reno.",
      icon: "ri-government-line",
    },
    {
      id: "svl-4",
      name: "Sparks Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "750 4th St",
      city: "Sparks",
      state: "NV",
      zip: "89431",
      phone: "(775) 359-9248",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Sparks — 12 miles southeast",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Sparks and eastern metro residents. Good alternative for Sun Valley residents whose commute takes them through Sparks.",
      icon: "ri-mail-line",
    },
    {
      id: "svl-5",
      name: "Spanish Springs Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "250 Los Altos Pkwy",
      city: "Sparks",
      state: "NV",
      zip: "89436",
      phone: "(775) 425-1060",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "Spanish Springs — 15 miles east",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Eastern valley option. Limited hours — call ahead for appointment. Serves Spanish Springs and eastern North Valleys.",
      icon: "ri-mail-line",
    },
    {
      id: "svl-6",
      name: "Cold Springs Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "18401 US-395",
      city: "Reno",
      state: "NV",
      zip: "89508",
      phone: "(775) 677-0474",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "Cold Springs — 7 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Northernmost acceptance facility in the Reno metro area. Convenient for Cold Springs and northern North Valleys residents. Limited hours.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Distance to SF Agency", agency: "230 miles — 4 hour drive via I-80 over Donner Pass, plus SF traffic and parking", fpc: "We drive for you — no travel needed" },
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-snowy-line",
      title: "Underestimating Donner Pass Winter Conditions for San Francisco Agency Drive",
      description: "Sun Valley residents attempting to drive to the San Francisco Passport Agency in winter face one of the most treacherous mountain highway sections in the country — Donner Pass on I-80. Snow, chain controls, and whiteout conditions can turn a 4-hour trip into 8+ hours or a complete road closure. Registered courier service via RNO air cargo eliminates this dangerous winter drive entirely.",
    },
    {
      icon: "ri-tools-line",
      title: "Tradespeople on International Jobs Not Maintaining Current Passports",
      description: "Sun Valley has a significant skilled trades workforce — electricians, pipefitters, welders, and construction professionals — who occasionally receive international job opportunities on short notice. Projects in Canada, overseas military bases, and international commercial sites often require immediate travel. Tradespeople should maintain current passports even without immediate travel plans — international jobs can materialize in days.",
    },
    {
      icon: "ri-road-map-line",
      title: "Assuming the Reno Passport Office Can Provide Same-Day Service",
      description: "The Reno Main Post Office and Washoe County Clerk are acceptance facilities only — they accept applications and forward them for processing. They cannot issue same-day passports. Sun Valley residents sometimes confuse acceptance facilities with passport agencies. The closest actual passport agency is in San Francisco — 230 miles and a mountain range away.",
    },
    {
      icon: "ri-plane-line",
      title: "Not Leveraging Reno-Tahoe International Airport (RNO) for Courier Routing",
      description: "RNO is just 10 minutes south of Sun Valley and provides reliable cargo service to the Bay Area. Sun Valley residents using courier services benefit from RNO's proximity — overnight document routing to San Francisco is fast and consistent. The airport's smaller size compared to LAS actually means faster cargo processing and fewer delays.",
    },
    {
      icon: "ri-image-line",
      title: "North Valleys Photo Services Producing Non-Compliant Passport Photos",
      description: "Photo services in the North Valleys and northern Reno area may not handle the volume of passport photos that ensure familiarity with current State Department standards. Photos must be exactly 2x2 inches with a pure white background, no glasses, neutral expression. Even slight deviations cause rejection. Use a high-volume pharmacy or UPS Store for passport photos rather than small convenience stores.",
    },
    {
      icon: "ri-global-line",
      title: "Not Checking Six-Month Passport Validity for Canadian Work Projects",
      description: "Sun Valley tradespeople working on Canadian projects — which often start on short notice — sometimes discover their passport is technically valid but doesn't meet Canada's 6-month validity preference. While Canada doesn't formally require 6 months for U.S. citizens, airlines and border agents may deny entry if your passport expires during the project timeline. Renew well in advance of any potential Canadian work.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop at any UPS location in Lemmon Valley, Stead, or Reno." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team reviews every document, then couriers to the San Francisco Passport Agency — via RNO air cargo, just 10 minutes from Sun Valley." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the San Francisco Passport Agency via our pre-secured courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Sun Valley or North Valleys address — fully trackable." },
  ],
  faqs: [
    {
      q: "How far is Sun Valley from the closest passport agency?",
      a: "Sun Valley is approximately 230 miles from the closest passport agency, the San Francisco Passport Agency at the Phillip Burton Federal Building on Golden Gate Avenue. There is no passport agency anywhere in Nevada. The drive takes about 4 hours via I-80 west over Donner Pass — which can be treacherous in winter months with chain controls and closures.",
    },
    {
      q: "Can I get a passport at the Lemmon Valley Post Office?",
      a: "Yes. The Lemmon Valley Post Office at 175 Lemmon Dr is the closest passport acceptance facility to Sun Valley, about 5 miles south. It accepts DS-11 applications but does not process or expedite passports. Call (775) 677-1212 for an appointment. Hours are limited — Monday through Friday, 9 AM to 3:30 PM.",
    },
    {
      q: "How fast can I get a passport in Sun Valley?",
      a: "Fast Passport Center's emergency service processes passports in 24–48 hours for Sun Valley residents via courier to San Francisco. Standard expedited service runs 2–9 business days. Reno-Tahoe International Airport (RNO) — just 10 minutes south of Sun Valley — provides reliable overnight cargo routing to the Bay Area year-round.",
    },
    {
      q: "Where can I get passport photos in Sun Valley or the North Valleys?",
      a: "The closest photo services are at Walgreens on 10500 N McCarran Blvd in northwest Reno (about 8 miles south) and CVS at 5005 Pyramid Lake Rd in Sparks (about 12 miles southeast). For Lemmon Valley residents, the Walgreens at 3495 S Virginia St in Reno is a reliable option. All must meet 2x2 inch white background specifications.",
    },
    {
      q: "I'm a tradesperson with a short-notice job in Canada — can you help fast?",
      a: "Absolutely. Sun Valley tradespeople — electricians, welders, pipefitters, and construction professionals — regularly use Fast Passport Center for rush passport processing before international job assignments. Our 2–9 business day courier service handles document review and secure submission. We understand that international jobs often come with minimal notice — we prioritize speed and accuracy.",
    },
    {
      q: "Is there a passport agency in Reno?",
      a: "No. Reno has passport acceptance facilities (Main Post Office on Vassar Street, Washoe County Clerk on E 9th Street) but no passport agency. These facilities accept applications and forward them for standard processing. The closest passport agency is in San Francisco — 230 miles west. For fast processing, courier service is the most practical option for Sun Valley and Reno-area residents.",
    },
    {
      q: "Can Fast Passport Center serve Cold Springs and Lemmon Valley too?",
      a: "Absolutely. We serve the entire North Valleys region including Sun Valley, Cold Springs, Lemmon Valley, Stead, Panther Valley, and surrounding communities. Document pickup is available throughout northern Washoe County. Our courier service is the same whether you're on Sun Valley Boulevard, Cold Springs Drive, or Lemmon Drive.",
    },
    {
      q: "Does winter weather affect courier service from Sun Valley?",
      a: "Winter weather in the Sierra Nevada can close I-80 at Donner Pass, but this does not affect air courier service from RNO. Reno-Tahoe International Airport operates reliably year-round regardless of mountain pass conditions, and overnight cargo flights to the Bay Area are rarely affected by Sierra weather. Air courier is far more reliable than driving over Donner Pass in winter.",
    },
    {
      q: "The Lemmon Valley Post Office passport appointments are booked — what are my other options?",
      a: "If Lemmon Valley is full, check the Reno Main Post Office (2000 Vassar St, 10 miles south), Sparks Post Office (750 4th St, 12 miles southeast), or Spanish Springs Post Office (250 Los Altos Pkwy, 15 miles east). The Washoe County Clerk at 1001 E 9th St in Reno also accepts passport applications with more appointment availability. For eligible DS-82 mail-in renewals, skip the acceptance facility entirely.",
    },
    {
      q: "I'm flying from RNO internationally — do I need to worry about passport validity?",
      a: "Yes. RNO offers connecting international service through major hubs. Your passport must meet the destination country's validity requirements — typically 6 months beyond your planned return date. Many Sun Valley residents discover at RNO check-in that their passport technically has time left but doesn't meet the 6-month rule. Check both the expiration date and the destination country's requirements well before booking.",
    },
    {
      q: "I work near Stead Airport — can you deliver there?",
      a: "Yes. Fast Passport Center delivers throughout the North Valleys including Sun Valley, Stead, Lemmon Valley, and Cold Springs. We use overnight courier with full tracking — your new passport can be delivered to your home address or workplace in the North Valleys. Stead Airport itself is a general aviation facility without commercial cargo, but RNO's cargo infrastructure handles all our North Valleys routing.",
    },
    {
      q: "How does the courier service work if I need to ship documents from Sun Valley?",
      a: "Fast Passport Center sends you a prepaid overnight shipping label. You can drop your documents at any UPS location in the North Valleys — the closest is the UPS Store at 5000 Smithridge Dr in Reno (about 10 miles south). Documents are then couriered overnight to San Francisco via RNO air cargo. Your new passport is returned via overnight delivery directly to your Sun Valley address with full tracking.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — N McCarran & Mae Anne", address: "10500 N McCarran Blvd, Reno, NV 89503", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Pyramid & Los Altos", address: "5005 Pyramid Lake Rd, Sparks, NV 89436", hours: "Mon–Sun: 8AM–9PM" },
    { name: "Walgreens — S Virginia & Plumb", address: "3495 S Virginia St, Reno, NV 89502", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — N Virginia & McCarran", address: "5019 N Virginia St, Reno, NV 89506", hours: "Mon–Sun: 8AM–9PM" },
    { name: "UPS Store — Smithridge & S Virginia", address: "5000 Smithridge Dr, Reno, NV 89502", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–2PM" },
    { name: "FedEx Office — Kietzke & Virginia", address: "5390 Kietzke Ln, Reno, NV 89511", hours: "Mon–Fri: 7:30AM–7PM, Sat: 9AM–5PM" },
  ],
  dmvLocations: [
    { name: "NV DMV — Reno", address: "305 Galletti Way, Reno, NV 89512", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Closest full-service DMV to Sun Valley. REAL ID, driver's license, and state ID services. About 12 miles south via US-395.", featured: true },
    { name: "NV DMV — Sparks", address: "9736 Pyramid Way, Sparks, NV 89441", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Eastern metro DMV. Convenient for Sun Valley residents who commute through Sparks. About 15 miles east.", featured: false },
    { name: "NV DMV — Carson City", address: "555 Wright Way, Carson City, NV 89711", hours: "Mon–Fri: 8:00AM–5:00PM", note: "State capital DMV. About 40 miles south of Sun Valley via US-395. Full services available.", featured: false },
  ],
  airports: [
    {
      code: "RNO",
      name: "Reno-Tahoe International Airport",
      distance: "10 miles south of Sun Valley",
      address: "2001 E Plumb Ln, Reno, NV 89502",
      note: "Northern Nevada's primary commercial airport with domestic and limited international service. About 15 minutes from Sun Valley via US-395. RNO offers connecting flights to major international hubs — the airport's cargo infrastructure makes courier routing to San Francisco fast and reliable.",
      flightUrl: "https://www.renoairport.com/",
      flightLabel: "RNO Airport",
      featured: true,
    },
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "230 miles southwest of Sun Valley",
      address: "San Francisco, CA 94128",
      note: "Co-located with the closest passport agency. About 4 hours driving via I-80 west over Donner Pass. Multiple daily flights from RNO to SFO take about 1 hour — the air route avoids Donner Pass entirely.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Airport",
      featured: false,
    },
    {
      code: "SMF",
      name: "Sacramento International Airport",
      distance: "140 miles southwest of Sun Valley",
      address: "6900 Airport Blvd, Sacramento, CA 95837",
      note: "Alternative international gateway about 30 minutes closer than SFO. About 2 hours 30 minutes driving via I-80 west. Offers domestic and some international routes — a practical alternative to SFO for North Valleys travelers.",
      flightUrl: "https://www.sacramento.aero/smf",
      flightLabel: "SMF Airport",
      featured: false,
    },
    {
      code: "RTS",
      name: "Reno Stead Airport",
      distance: "5 miles west of Sun Valley",
      address: "4895 Texas Ave, Reno, NV 89506",
      note: "General aviation airport in Stead — home of the National Championship Air Races. No commercial passenger service, but one of the busiest general aviation facilities in the region. Not relevant for passport or commercial international travel.",
      flightUrl: "https://www.renoairport.com/stead",
      flightLabel: "RTS Airport",
      featured: false,
    },
  ],
};